import { Grid } from '@mui/material';
import { orderBy } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '@callmart/types';
import { WorktypeSelectOptions } from '../../enums/WorktypeSelectOptions';
import { RecordCreatedAt } from '../../enums/RecordCreatedAt';
import {
    useTotalForecastDeltasByDateRangeAndWorktype,
    useTotalBookedBookingsByDateRangeAndWorktype,
    useAllBookedBookingsByDateRangeAndWorktype,
    useAllForecastDeltasByDateRangeAndWorktype,
    useSumOfBookedBookingsByDateAndWorktypeGroupByLocation,
    useHEMTLocations,
    useAllDistinctLocations,
    useLocationsByWorktype,
    useLatestLocationDeltaUploadDateForAllLocations,
} from '../../hooks';
import { createTableRows, combineChartDataInOneArray } from '../../utils/agGridUtils';
import { integerValueFormatter } from '../../utils/numberUtils';
import { useLastCapacityReportUploadDatePerLocation } from '../../hooks/api/capacityReports';
import { useTotalEventsForAllLocations } from '../../hooks/api/events';
import DashboardTotals from './DashboardTotals';
import DashboardChart from './DashboardChart';
import DashboardTable from './DashboardTable';
import DashboardTEFTableCard from './DashboardTEFTableCard';
import { createMapWithTableRows, combineLatestAndOldestBookings, createColumnDefs } from './helpers/utils';
import dayjs, { formatSlotTime } from '@utils/dayjs';

const getTableRowsForCommonTables = (allLocationsFromHEMT, records, valueType = 'latestUpload') => {
    let rows = [];
    for (const hemtLocation of allLocationsFromHEMT) {
        const data = records.find((loc) => loc.locationId === hemtLocation.id);
        rows.push({
            cityCode: hemtLocation.code,
            cityName: hemtLocation.city,
            ...(valueType === 'latestUpload' && { latestUpload: data?.value ? dayjs(data.value) : null }),
            value: !data?.value
                ? ''
                : valueType === 'totalEvents'
                  ? data.value
                  : formatSlotTime(data?.value, `${DATE_FORMATS.DATE} ${DATE_FORMATS.TIME}`),
        });
    }

    rows = orderBy(
        rows,
        [
            (row) => {
                const valueToOrderBy = valueType === 'latestUpload' ? row.latestUpload : row.value;
                return valueToOrderBy || '';
            },
        ],
        ['desc']
    );

    return rows;
};

function DashboardTEF({ dateFrom, dateTo, worktype, showSlotIntervalOnChart }) {
    const { t } = useTranslation('dashboard');
    // Column defs
    const allLocationsFromHEMT = useHEMTLocations();
    const allDistinctLocations = useAllDistinctLocations();
    const locationsByWorktype = useLocationsByWorktype(worktype);
    const locationsForColumnDefs =
        worktype === WorktypeSelectOptions.ALL_WORKTYPES.value ? allDistinctLocations : locationsByWorktype;
    const columnDefs =
        allLocationsFromHEMT && locationsForColumnDefs?.length > 0
            ? createColumnDefs(
                  locationsForColumnDefs,
                  {
                      fieldPrefix: 'locationId',
                      idField: 'locationId',
                      getHeaderName: (location) => {
                          const locationsFromHEMT = allLocationsFromHEMT.find((loc) => loc.id === location.locationId);
                          return locationsFromHEMT ? locationsFromHEMT.city : location.id;
                      },
                  },
                  t
              )
            : [];

    // Totals
    const totalForecastDeltas = useTotalForecastDeltasByDateRangeAndWorktype({
        dateFrom,
        dateTo,
        worktype,
    });
    const totalBookedBookings = useTotalBookedBookingsByDateRangeAndWorktype({
        dateFrom,
        dateTo,
        worktype,
    });
    const totals = useMemo(
        () => ({
            totalForecastDeltas,
            totalBookedBookings,
            totalAvailableBookings: {
                positive: (totalForecastDeltas?.positive || 0) - (totalBookedBookings?.positive || 0),
                negative: (totalForecastDeltas?.negative || 0) - (totalBookedBookings?.negative || 0),
            },
        }),
        [totalBookedBookings, totalForecastDeltas]
    );

    // Chart data
    const [bookedCalls] = useAllBookedBookingsByDateRangeAndWorktype({ dateFrom, dateTo, worktype });
    const [forecastDeltaCalls] = useAllForecastDeltasByDateRangeAndWorktype({
        dateFrom,
        dateTo,
        worktype,
    });

    const chartData = useMemo(
        () => combineChartDataInOneArray(bookedCalls, forecastDeltaCalls),
        [bookedCalls, forecastDeltaCalls]
    );

    // Table data
    const [latestBookedBookings] = useSumOfBookedBookingsByDateAndWorktypeGroupByLocation({
        dateFrom,
        dateTo,
        worktype,
        recordCreatedAt: RecordCreatedAt.LATEST,
    });
    const [oldestBookedBooking] = useSumOfBookedBookingsByDateAndWorktypeGroupByLocation({
        dateFrom,
        dateTo,
        worktype,
        recordCreatedAt: RecordCreatedAt.OLDEST,
    });
    const bookings = useMemo(
        () => combineLatestAndOldestBookings(latestBookedBookings, oldestBookedBooking, 'locationId'),
        [latestBookedBookings, oldestBookedBooking]
    );
    const tableRows = useMemo(
        () => createTableRows(createMapWithTableRows(bookings, 'locationId'), 'bookingType'),
        [bookings]
    );

    // Last upload and total events
    const tefTablesCommonColumnDefs = useMemo(
        () => [
            {
                field: 'city',
                headerName: t('table.location'),
                valueGetter: (params) => `${params.data.cityName} - ${params.data.cityCode}`,
            },
            {
                field: 'value',
                headerName: t('table.lastReport'),
            },
        ],
        [t]
    );
    const [latestCapacityReportUploadDatesPerLocation] = useLastCapacityReportUploadDatePerLocation();
    const latestCapacityReportUploadRows = useMemo(
        () =>
            getTableRowsForCommonTables(
                allLocationsFromHEMT,
                latestCapacityReportUploadDatesPerLocation,
                'latestUpload'
            ),
        [allLocationsFromHEMT, latestCapacityReportUploadDatesPerLocation]
    );

    const [latestLocationDeltaUploadDatesPerLocation] = useLatestLocationDeltaUploadDateForAllLocations();
    const latestLocationDeltaUploadRows = useMemo(
        () =>
            getTableRowsForCommonTables(
                allLocationsFromHEMT,
                latestLocationDeltaUploadDatesPerLocation,
                'latestUpload'
            ),
        [allLocationsFromHEMT, latestLocationDeltaUploadDatesPerLocation]
    );
    const [totalEventsPerLocation] = useTotalEventsForAllLocations();
    const totalEventsRows = useMemo(
        () => getTableRowsForCommonTables(allLocationsFromHEMT, totalEventsPerLocation, 'totalEvents'),
        [allLocationsFromHEMT, totalEventsPerLocation]
    );

    return (
        <>
            <Grid item xs={12} sm={12} md={7} lg={3}>
                <DashboardTotals totals={totals}></DashboardTotals>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={5}>
                <DashboardChart chartData={chartData} showSlotInterval={showSlotIntervalOnChart}></DashboardChart>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} sx={{ mt: 2, mb: 2 }}>
                <DashboardTable tableRows={tableRows} columnDefs={columnDefs}></DashboardTable>
            </Grid>
            <DashboardTEFTableCard
                title={t('tef.deltaReports')}
                tableRows={latestLocationDeltaUploadRows}
                columnDefs={tefTablesCommonColumnDefs}
            ></DashboardTEFTableCard>
            <DashboardTEFTableCard
                title={t('tef.potentialReports')}
                tableRows={latestCapacityReportUploadRows}
                columnDefs={tefTablesCommonColumnDefs}
            ></DashboardTEFTableCard>
            <DashboardTEFTableCard
                title={t('tef.events')}
                tableRows={totalEventsRows}
                // Only this card's `value` is a number — the two above render a formatted upload date
                // through the same shared column, so the formatter belongs here and not in it.
                columnDefs={[
                    tefTablesCommonColumnDefs[0],
                    { field: 'value', headerName: t('table.total'), valueFormatter: integerValueFormatter },
                ]}
            ></DashboardTEFTableCard>
        </>
    );
}

export default DashboardTEF;
